import { useMutation, useQuery } from '@apollo/client';
import { ArrowUpRightIcon, DocumentTextIcon } from '@heroicons/react/20/solid';
import { gql } from '__generated__/gql';
import { PortfolioUpdateRequestStatusEnumType } from '__generated__/graphql';
import dayjs from 'dayjs';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';

import Button from 'primitives/Button';
import EmptyState from 'primitives/EmptyState';
import LoadingIndicator from 'primitives/LoadingIndicator';

import PostSlideOver from '../../Invest/Posts/SyndicatePosts/PostSlideOver';
import ArchiveRequestDialog from './ArchiveRequestDialog';

const GET_OR_CREATE_TOKEN = gql(`
  mutation GetOrCreatePortfolioUpdateToken($dealId: ID!) {
    getOrCreatePortfolioUpdateToken(dealId: $dealId)
  }
`);

const UPDATE_REQUESTS_QUERY = gql(`
  query SyndicateUpdateRequests($syndicateId: ID!) {
    syndicate(id: $syndicateId) {
      id
      deals {
        pageInfo {
          cursor
          hasNextPage
        }
        nodes {
          id
          companyInformation {
            company {
              id
              name
              image
            }
          }
          portfolioUpdateRequests {
            nodes {
              id
              status
              leadNote
              createdAt
              requester {
                id
                name
              }
              recentDealPosts(limit: 3) {
                id
                title
                publishedAt
              }
            }
            pageInfo {
              cursor
              hasNextPage
            }
          }
        }
      }
    }
  }
`);

function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const now = new Date();
  const past = new Date(dateStr);
  const diffMs = now.getTime() - past.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  if (diffDays < 30) return `${diffDays} days ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths === 1) return '1 month ago';
  if (diffMonths < 12) return `${diffMonths} months ago`;
  const diffYears = Math.floor(diffMonths / 12);
  return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`;
}

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();
}

const STATUS_CONFIG: Record<
  PortfolioUpdateRequestStatusEnumType,
  {
    label: string;
    badge: string;
    border: string;
    bg: string;
    avatar: string;
  }
> = {
  [PortfolioUpdateRequestStatusEnumType.Pending]: {
    label: 'Pending',
    badge: 'bg-yellow-100 text-yellow-800 ring-1 ring-yellow-200',
    border: 'border-l-yellow-400',
    bg: 'bg-yellow-50/40',
    avatar: 'bg-yellow-100 text-yellow-700',
  },
  [PortfolioUpdateRequestStatusEnumType.Acknowledged]: {
    label: 'Acknowledged',
    badge: 'bg-blue-100 text-blue-800 ring-1 ring-blue-200',
    border: 'border-l-blue-400',
    bg: 'bg-blue-50/40',
    avatar: 'bg-blue-100 text-blue-700',
  },
  [PortfolioUpdateRequestStatusEnumType.Archived]: {
    label: 'Archived',
    badge: 'bg-gray-100 text-gray-500 ring-1 ring-gray-200',
    border: 'border-l-gray-300',
    bg: 'bg-gray-50/60',
    avatar: 'bg-gray-200 text-gray-500',
  },
  [PortfolioUpdateRequestStatusEnumType.Fulfilled]: {
    label: 'Fulfilled',
    badge: 'bg-green-100 text-green-800 ring-1 ring-green-200',
    border: 'border-l-green-400',
    bg: 'bg-green-50/30',
    avatar: 'bg-green-100 text-green-700',
  },
};

const SyndicateUpdateRequests: FC = () => {
  const { syndicateId } = useParams<{ syndicateId: string }>() as { syndicateId: string };
  const [postOnDisplay, setPostOnDisplay] = useState('');
  const [getOrCreateToken] = useMutation(GET_OR_CREATE_TOKEN);

  const openForm = async (dealId: string) => {
    const result = await getOrCreateToken({ variables: { dealId } });
    const token = result.data?.getOrCreatePortfolioUpdateToken;
    if (token) window.open(`/portfolio-update/submit/${token}`, '_blank');
  };

  const { loading, error, data, refetch } = useQuery(UPDATE_REQUESTS_QUERY, {
    variables: { syndicateId },
  });

  if (loading) return <LoadingIndicator />;
  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const deals = data.syndicate.deals.nodes;
  const dealsWithRequests = deals.filter(deal => deal.portfolioUpdateRequests.nodes.length > 0);

  return (
    <>
      <PostSlideOver
        open={!!postOnDisplay}
        postId={postOnDisplay}
        onClose={() => setPostOnDisplay('')}
      />

      <SidebarLayout>
        <SidebarLayoutNavigation
          title="Update Requests"
          subTitle="Investor and admin requests for portfolio company updates."
        />
        <SidebarLayoutContent>
          {dealsWithRequests.length === 0 ? (
            <EmptyState
              title="No update requests"
              description="When investors or admins request a portfolio update, they'll appear here."
            />
          ) : (
            <div className="space-y-8">
              {dealsWithRequests.map(deal => (
                <div
                  key={deal.id}
                  className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
                >
                  {/* Company header */}
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {deal.companyInformation.company.image ? (
                        <img
                          src={deal.companyInformation.company.image}
                          alt={deal.companyInformation.company.name}
                          className="h-9 w-9 rounded-lg object-cover ring-1 ring-gray-200"
                        />
                      ) : (
                        <div className="h-9 w-9 rounded-lg bg-gray-200 flex items-center justify-center">
                          <span className="text-xs font-semibold text-gray-500">
                            {deal.companyInformation.company.name[0]}
                          </span>
                        </div>
                      )}
                      <h3 className="text-base font-semibold text-gray-900">
                        {deal.companyInformation.company.name}
                      </h3>
                      <span className="text-xs text-gray-400 font-normal">
                        {deal.portfolioUpdateRequests.nodes.length}{' '}
                        {deal.portfolioUpdateRequests.nodes.length === 1 ? 'request' : 'requests'}
                      </span>
                    </div>
                    <Button variant="secondary" onClick={() => openForm(deal.id)}>
                      Create Update
                    </Button>
                  </div>

                  {/* Requests */}
                  <div className="divide-y divide-gray-100">
                    {deal.portfolioUpdateRequests.nodes.map(request => {
                      const cfg = STATUS_CONFIG[request.status];
                      const requesterName = request.requester?.name ?? 'Platform Admin';
                      const isPending =
                        request.status === PortfolioUpdateRequestStatusEnumType.Pending;
                      const isArchived =
                        request.status === PortfolioUpdateRequestStatusEnumType.Archived;

                      return (
                        <div key={request.id} className={`flex border-l-4 ${cfg.border} ${cfg.bg}`}>
                          <div className="flex-1 px-5 py-4 space-y-3 min-w-0">
                            {/* Top row: avatar + name + status + time */}
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <span
                                  className={`flex-shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${cfg.avatar}`}
                                >
                                  {initials(requesterName)}
                                </span>
                                <span className="text-sm font-semibold text-gray-800 truncate">
                                  {requesterName}
                                </span>
                                <span
                                  className={`flex-shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cfg.badge}`}
                                >
                                  {cfg.label}
                                </span>
                                <span className="flex-shrink-0 text-xs text-gray-400">
                                  {timeAgo(request.createdAt)}
                                </span>
                              </div>

                              {isPending && (
                                <ArchiveRequestDialog
                                  requestId={request.id}
                                  onDone={() => refetch()}
                                />
                              )}
                            </div>

                            {/* Lead note */}
                            {isArchived && request.leadNote && (
                              <div className="rounded-lg bg-white border border-gray-200 px-3 py-2.5">
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                                  Your note
                                </p>
                                <p className="text-sm text-gray-700 leading-relaxed">
                                  {request.leadNote}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Recent posts — shown once per deal card */}
                  {(() => {
                    const posts = deal.portfolioUpdateRequests.nodes[0]?.recentDealPosts ?? [];
                    if (posts.length === 0) return null;
                    return (
                      <div className="px-5 py-4 border-t border-gray-100 space-y-2">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          Recent posts
                        </p>
                        <div className="space-y-1">
                          {posts.map(post => (
                            <button
                              key={post.id}
                              type="button"
                              onClick={() => setPostOnDisplay(post.id)}
                              className="w-full flex items-center gap-2.5 rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-left hover:border-indigo-300 hover:bg-indigo-50/50 transition-colors group"
                            >
                              <DocumentTextIcon className="h-4 w-4 flex-shrink-0 text-gray-400 group-hover:text-indigo-500 transition-colors" />
                              <span className="flex-1 text-sm font-medium text-gray-700 truncate group-hover:text-indigo-700 transition-colors">
                                {post.title}
                              </span>
                              <span className="flex-shrink-0 text-xs text-gray-400 tabular-nums">
                                {post.publishedAt
                                  ? dayjs(post.publishedAt).format('MMM D, YYYY')
                                  : '—'}
                              </span>
                              <ArrowUpRightIcon className="h-3.5 w-3.5 flex-shrink-0 text-gray-300 group-hover:text-indigo-400 transition-colors" />
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
        </SidebarLayoutContent>
      </SidebarLayout>
    </>
  );
};

export default SyndicateUpdateRequests;
